import { useState, useEffect } from 'react';
import {
  DevDataPoint,
  AggregatedCommit,
  DevTotalCommits,
  RepoFilter,
  CommitEntry,
  DashboardProps,
  DevLocationData,
  RepoData
} from './types';

import { theme } from './theme';
import Leaderboard from './Leaderboard';
import RepoLeaderboard from './RepoLeaderboard';
import CommitDevRepo from './CommitDevRepo';
import Header from './Header';
import Footer from './Footer';
import ActiveDevs from './ActiveDevs';
import DevLocationMap from './DevLocationMap';
import {
  processDataByTimeFilter,
  filterDataByDateRange,
  isMonthEnd,
  parseCSVData,
  getMaxDate,
  fetchCSVURL,
  fetchDevsCSV,
  parseDevsCSV,
  fetchDevsLocCSV,
  fetchLast1YDevsLocCSV,
  parseDevsLocCSV,
  fetchReposCSV,
  parseReposCSV,
  FILTERED_USERS
} from './utils';

interface DevDashboardProps {
  showActiveDevs: boolean;
  showLeaderboard: boolean;
}

const DevDashboard: React.FC<DevDashboardProps> = ({ showActiveDevs, showLeaderboard }) => {
  // State for our data, slider, and theme
  const [data, setData] = useState<DevDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date, Date]>([new Date(), new Date()]);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date] | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(document.body.classList.contains('dark-mode'));
  const [commitData, setCommitData] = useState<AggregatedCommit[]>([]);
  const [devLeaderboardData, setDevLeaderboardData] = useState<DevTotalCommits[]>([]);
  const [commitLoading, setCommitLoading] = useState<boolean>(true);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'last30d' | 'last90d' | 'last1y' | 'allTime'>('last30d');
  const [repoFilter, setRepoFilter] = useState<RepoFilter>('all');
  const [repoSearchFilter, setRepoSearchFilter] = useState<string>('');
  const [parsedData, setParsedData] = useState<CommitEntry[]>([]);
  const [sliderValue, setSliderValue] = useState<number>(20);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [devFilter, setDevFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'dev' | 'repo' | 'commits'>('commits');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Map data state
  const [devsLocData, setDevsLocData] = useState<DevLocationData[]>([]);
  const [lastYearDevsLocData, setLastYearDevsLocData] = useState<DevLocationData[]>([]);

  // Repo data state
  const [repoData, setRepoData] = useState<RepoData[]>([]);
  const [repoLoading, setRepoLoading] = useState<boolean>(true);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [repoSortBy, setRepoSortBy] = useState<'stars' | 'forks' | 'issues'>('stars');
  const [repoSortOrder, setRepoSortOrder] = useState<'asc' | 'desc'>('desc');

  // Separate filter states for CommitDevRepo
  const [commitTimeFilter, setCommitTimeFilter] = useState<'last30d' | 'last90d' | 'last1y' | 'allTime'>('last30d');
  const [commitRepoFilter, setCommitRepoFilter] = useState<RepoFilter>('all');

  const currentTheme = darkMode ? theme.dark : theme.light;

  // Handle time filter change
  const handleTimeFilterChange = (filter: 'last30d' | 'last90d' | 'last1y' | 'allTime') => {
    setTimeFilter(filter);
    const processedData = processDataByTimeFilter(parsedData, filter, maxDate, repoFilter);
    setDevLeaderboardData(processedData.devs);
    setCommitData(processedData.commits);
  };

  // Handle repo filter change
  const handleRepoFilterChange = (value: RepoFilter | ((prev: RepoFilter) => RepoFilter)) => {
    const newFilter = typeof value === 'function' ? value(repoFilter) : value;
    setRepoFilter(newFilter);
    const processedData = processDataByTimeFilter(parsedData, timeFilter, maxDate, newFilter);
    setDevLeaderboardData(processedData.devs);
    setCommitData(processedData.commits);
  };

  // Handle commit time filter change (separate from leaderboard)
  const handleCommitTimeFilterChange = (filter: 'last30d' | 'last90d' | 'last1y' | 'allTime') => {
    setCommitTimeFilter(filter);
    const processedData = processDataByTimeFilter(parsedData, filter, maxDate, commitRepoFilter);
    setCommitData(processedData.commits);
  };

  // Handle commit repo filter change (separate from leaderboard)
  const handleCommitRepoFilterChange = (value: RepoFilter | ((prev: RepoFilter) => RepoFilter)) => {
    const newFilter = typeof value === 'function' ? value(commitRepoFilter) : value;
    setCommitRepoFilter(newFilter);
    const processedData = processDataByTimeFilter(parsedData, commitTimeFilter, maxDate, newFilter);
    setCommitData(processedData.commits);
  };

  // Handle repo sort change
  const handleRepoSortChange = (column: 'stars' | 'forks' | 'issues') => {
    if (repoSortBy === column) {
      setRepoSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRepoSortBy(column);
      setRepoSortOrder('desc');
    }
  };

  // Fetch commit data from CSV
  useEffect(() => {
    const fetchCommitData = async () => {
      try {
        setCommitLoading(true);

        const csvResponse = await fetchCSVURL();
        const csvText = csvResponse.data;

        // Parse CSV data
        const parsed = parseCSVData(csvText);

        // Find max date in the data
        const maxDateValue = getMaxDate(parsed);

        if (!maxDateValue) {
          throw new Error("No valid dates found in data");
        }

        setMaxDate(maxDateValue);
        setParsedData(parsed);

        // Process the data
        const processedData = processDataByTimeFilter(parsed, timeFilter, maxDateValue, repoFilter);
        setDevLeaderboardData(processedData.devs);
        setCommitData(processedData.commits);
      } catch (err) {
        setCommitError("Error fetching commit data.");
        console.error("Error fetching commit data:", err);
      } finally {
        setCommitLoading(false);
      }
    };

    fetchCommitData();
  }, [timeFilter, repoFilter]);

  // Fetch data from S3
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const dataResponse = await fetchDevsCSV();
        const csvText = dataResponse.data;

        // Parse CSV data directly into DevDataPoint objects
        const formattedData = parseDevsCSV(csvText);

        // Sort by date
        formattedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        setData(formattedData);

        // Set initial date range based on the data
        if (formattedData.length > 0) {
          const startDate = new Date(formattedData[0].date);
          const endDate = new Date(formattedData[formattedData.length - 1].date);
          setDateRange([startDate, endDate]);
        }
      } catch (err) {
        setError("Error fetching data. Please check your URL and try again.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch Map Data
  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const [devsLocResponse, lastYearDevsLocResponse] = await Promise.all([
          fetchDevsLocCSV(),
          fetchLast1YDevsLocCSV()
        ]);

        const devsLocParsed = parseDevsLocCSV(devsLocResponse.data);
        const lastYearDevsLocParsed = parseDevsLocCSV(lastYearDevsLocResponse.data);

        setDevsLocData(devsLocParsed);
        setLastYearDevsLocData(lastYearDevsLocParsed);
      } catch (err) {
        console.error("Error fetching map data:", err);
        // Don't block the main dashboard for map errors
      }
    };

    fetchMapData();
  }, []);

  // Fetch Repo Data
  useEffect(() => {
    const fetchRepoDataAsync = async () => {
      try {
        setRepoLoading(true);
        const repoResponse = await fetchReposCSV();
        const repoParsed = parseReposCSV(repoResponse.data);
        setRepoData(repoParsed);
      } catch (err) {
        console.error("Error fetching repo data:", err);
        setRepoError("Error fetching repository data.");
      } finally {
        setRepoLoading(false);
      }
    };

    fetchRepoDataAsync();
  }, []);

  // Get filtered data based on slider value
  const filteredData = () => {
    return filterDataByDateRange(data, dateRange, sliderValue);
  };

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderValue(parseFloat(e.target.value));
  };

  // Handle sort change
  const handleSortChange = (columnName: 'dev' | 'repo' | 'commits') => {
    if (sortBy === columnName) {
      // Toggle sort order if clicking the same column
      setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending order
      setSortBy(columnName);
      setSortOrder('desc');
    }
  };

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setDarkMode(document.body.classList.contains('dark-mode'));
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: currentTheme.bg }}>
        <div className="text-xl" style={{ color: currentTheme.primary }}>Loading data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: currentTheme.bg }}>
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  const displayData = filteredData();

  return (
    <div className="p-3 md:p-6 mx-auto w-full rounded-lg shadow-lg" style={{ backgroundColor: currentTheme.bg }}>
      {/* Header with Algorand Logo */}
      <Header currentTheme={currentTheme} />

      {/* Active Developers Chart */}
      {showActiveDevs && (
        <>
          <ActiveDevs
            data={data}
            displayData={displayData}
            dateRange={dateRange}
            sliderValue={sliderValue}
            currentTheme={currentTheme}
            handleSliderChange={handleSliderChange}
            isMonthEnd={isMonthEnd}
          />

          <DevLocationMap
            allTimeData={devsLocData}
            lastYearData={lastYearDevsLocData}
            currentTheme={currentTheme}
            darkMode={darkMode}
          />
        </>
      )}

      {/* Developer Leaderboard and Table */}
      {showLeaderboard && (
        <>
          <Leaderboard
            devLeaderboardData={devLeaderboardData}
            currentTheme={currentTheme}
            timeFilter={timeFilter}
            handleTimeFilterChange={handleTimeFilterChange}
            repoFilter={repoFilter}
            setRepoFilter={handleRepoFilterChange}
            commitLoading={commitLoading}
            commitError={commitError}
          />

          <RepoLeaderboard
            repoData={repoData}
            currentTheme={currentTheme}
            sortBy={repoSortBy}
            sortOrder={repoSortOrder}
            handleSortChange={handleRepoSortChange}
            loading={repoLoading}
            error={repoError}
            repoSearchFilter={repoSearchFilter}
            setRepoSearchFilter={setRepoSearchFilter}
          />

          <CommitDevRepo
            commitData={commitData}
            currentTheme={currentTheme}
            repoFilter={commitRepoFilter}
            setRepoFilter={handleCommitRepoFilterChange}
            commitLoading={commitLoading}
            commitError={commitError}
            devFilter={devFilter}
            setDevFilter={setDevFilter}
            sortBy={sortBy}
            sortOrder={sortOrder}
            handleSortChange={handleSortChange}
            timeFilter={commitTimeFilter}
            handleTimeFilterChange={handleCommitTimeFilterChange}
          />
        </>
      )}

      {/* Footer */}
      <Footer currentTheme={currentTheme} />
    </div>
  );
}

export default DevDashboard;